import { jsx, jsxs } from "react/jsx-runtime";
import { Button, FilterChip, Heading, Popover, PopupHeader, Slider, Span } from "@pcln/horizon";
import { forwardRef, useEffect, useState } from "react";
const DEFAULT_PRICE_FILTER_CEILING = 1500;
const PRICE_GUIDANCE_URL = '/pws/v0/htl/hotels-price-guide/priceguidance';
const PRICE_GUIDANCE_TIMEOUT_MS = 2000;
const CURRENCY_BY_COUNTRY = {
    AE: 'AED',
    AT: 'EUR',
    AU: 'AUD',
    BE: 'EUR',
    BR: 'BRL',
    CA: 'CAD',
    CH: 'CHF',
    CN: 'CNY',
    CY: 'EUR',
    CZ: 'CZK',
    DE: 'EUR',
    DK: 'DKK',
    EE: 'EUR',
    ES: 'EUR',
    FI: 'EUR',
    FR: 'EUR',
    GB: 'GBP',
    GR: 'EUR',
    HK: 'HKD',
    HU: 'HUF',
    ID: 'IDR',
    IE: 'EUR',
    IL: 'ILS',
    IN: 'INR',
    IT: 'EUR',
    JP: 'JPY',
    KR: 'KRW',
    LT: 'EUR',
    LU: 'EUR',
    LV: 'EUR',
    MT: 'EUR',
    MX: 'MXN',
    MY: 'MYR',
    NL: 'EUR',
    NO: 'NOK',
    NZ: 'NZD',
    PH: 'PHP',
    PL: 'PLN',
    PT: 'EUR',
    SE: 'SEK',
    SG: 'SGD',
    SI: 'EUR',
    SK: 'EUR',
    TH: 'THB',
    TR: 'TRY',
    TW: 'TWD',
    UK: 'GBP',
    US: 'USD',
    ZA: 'ZAR'
};
function resolveSessionCurrency(countryCode) {
    const cookieMatch = "u" > typeof document ? document.cookie.match(/(?:^|;)\s?selcur=(.*?)(?:;|$)/i) : null;
    const selcur = cookieMatch?.[1] ? decodeURI(cookieMatch[1]).trim().toUpperCase() : '';
    if (selcur) return selcur;
    const country = (countryCode || ("u" > typeof navigator ? navigator.language.split('-')[1] : '') || 'US').toUpperCase();
    return CURRENCY_BY_COUNTRY[country] ?? 'USD';
}
const CURRENCY_SYMBOL_CACHE = new Map([
    [
        'CAD',
        'C$'
    ]
]);
function currencySymbolForCode(currency) {
    const cached = CURRENCY_SYMBOL_CACHE.get(currency);
    if (cached) return cached;
    const symbol = new Intl.NumberFormat('en', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol'
    }).formatToParts(0).find((part)=>'currency' === part.type)?.value || currency;
    CURRENCY_SYMBOL_CACHE.set(currency, symbol);
    return symbol;
}
function extractPriceFilterCeiling(response, checkIn, checkOut) {
    const datePriceMap = Array.isArray(response?.data) ? response.data[0]?.datePriceMap : response?.data?.datePriceMap;
    if (!datePriceMap) return;
    let highest = 0;
    let currency = '';
    for (const [night, rows] of Object.entries(datePriceMap))if (!(night < checkIn) && !(night >= checkOut)) {
        for (const row of rows)if ('RTL' === row.product && 'All_Star' === row.starRating && (row.maxPriceTotal ?? 0) > highest) {
            highest = row.maxPriceTotal ?? 0;
            currency = row.currency ?? currency;
        }
    }
    return highest > 0 ? {
        ceiling: 100 * Math.ceil(highest / 100),
        currency
    } : void 0;
}
const PriceFilterControl_PriceFilterChipTrigger = /*#__PURE__*/ forwardRef(function({ selected, onClick, onMouseDown, ...triggerProps }, ref) {
    return /*#__PURE__*/ jsx(FilterChip, {
        ...triggerProps,
        ref: ref,
        opens: false,
        type: "chip",
        filterType: "radio",
        emphasis: "regular",
        showCheckIcon: false,
        className: selected ? 'openable-filter-chip w-fit self-start !border-transparent !bg-primary-3 hover:!bg-primary-4 active:!bg-primary-4' : 'openable-filter-chip w-fit self-start !border-primary-6 !bg-neutral-1 hover:!bg-neutral-1 active:!bg-neutral-1',
        label: "Price Filter (optional)",
        iconLeft: selected ? 'check' : 'tune',
        selected: selected,
        onClick: onClick,
        onMouseDown: onMouseDown
    });
});
function PriceFilterControl({ cityId, checkIn, checkOut, isMobile = false, url = PRICE_GUIDANCE_URL, timeout = PRICE_GUIDANCE_TIMEOUT_MS, countryCode, ceiling: ceilingProp, currencySymbol: currencySymbolProp, applied, onApply }) {
    const sessionCurrency = resolveSessionCurrency(countryCode);
    const [fetched, setFetched] = useState(null);
    const ceiling = ceilingProp ?? fetched?.ceiling ?? DEFAULT_PRICE_FILTER_CEILING;
    const currencySymbol = currencySymbolProp ?? fetched?.currencySymbol ?? currencySymbolForCode(sessionCurrency);
    const hasDestination = Boolean(cityId);
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState([
        0,
        ceiling
    ]);
    useEffect(()=>{
        setFetched(null);
        if (void 0 !== ceilingProp || !cityId || !checkIn || !checkOut) return;
        let cancelled = false;
        const controller = new AbortController();
        const timeoutId = setTimeout(()=>controller.abort(), timeout);
        const params = new URLSearchParams({
            appc: isMobile ? 'MOBILEWEB' : 'DESKTOP',
            currency: sessionCurrency,
            endDate: checkOut,
            entityId: cityId,
            entityType: 'CITY',
            product: 'RTL',
            starRating: 'all',
            startDate: checkIn
        });
        fetch(`${url}?${params.toString()}`, {
            signal: controller.signal
        }).then((res)=>res.ok ? res.json() : void 0).then((body)=>{
            if (cancelled || controller.signal.aborted) return;
            const result = extractPriceFilterCeiling(body, checkIn, checkOut);
            setFetched(result ? {
                ceiling: result.ceiling,
                currencySymbol: currencySymbolForCode(result.currency || sessionCurrency)
            } : null);
        }).catch(()=>{
            if (!cancelled) setFetched(null);
        }).finally(()=>clearTimeout(timeoutId));
        return ()=>{
            cancelled = true;
            clearTimeout(timeoutId);
            controller.abort();
        };
    }, [
        ceilingProp,
        cityId,
        checkIn,
        checkOut,
        isMobile,
        sessionCurrency,
        timeout,
        url
    ]);
    useEffect(()=>{
        setDraft([
            0,
            ceiling
        ]);
    }, [
        ceiling,
        currencySymbol
    ]);
    const low = draft[0].toLocaleString('en-US');
    const high = draft[1].toLocaleString('en-US');
    const rangeLabel = `${currencySymbol}${low} to ${currencySymbol}${draft[1] >= ceiling ? `${high}+` : high}`;
    return /*#__PURE__*/ jsxs(Popover.Root, {
        open: open,
        onOpenChange: (nextOpen)=>{
            if (nextOpen) setDraft([
                applied?.minPrice ?? 0,
                applied?.maxPrice ?? ceiling
            ]);
            setOpen(nextOpen);
        },
        children: [
            /*#__PURE__*/ jsx(Popover.Trigger, {
                openOnHover: false,
                render: /*#__PURE__*/ jsx(PriceFilterControl_PriceFilterChipTrigger, {
                    selected: Boolean(applied)
                })
            }),
            /*#__PURE__*/ jsx(Popover.Portal, {
                children: /*#__PURE__*/ jsx(Popover.Positioner, {
                    side: "bottom",
                    align: "start",
                    sideOffset: 8,
                    children: /*#__PURE__*/ jsxs(Popover.Popup, {
                        size: "md",
                        children: [
                            /*#__PURE__*/ jsxs(PopupHeader.Root, {
                                children: [
                                    /*#__PURE__*/ jsx(PopupHeader.Content, {
                                        children: /*#__PURE__*/ jsx(PopupHeader.Title, {
                                            asTitle: Popover.Title,
                                            children: "Price Filter"
                                        })
                                    }),
                                    /*#__PURE__*/ jsx(Popover.Close, {
                                        render: /*#__PURE__*/ jsx(PopupHeader.Close, {
                                            "aria-label": "Close price filter"
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ jsx(Popover.Body, {
                                children: hasDestination ? /*#__PURE__*/ jsxs("div", {
                                    className: "flex flex-col gap-3",
                                    children: [
                                        /*#__PURE__*/ jsx(Heading, {
                                            as: "h3",
                                            textStyle: "heading5",
                                            children: "Your budget (per night)"
                                        }),
                                        /*#__PURE__*/ jsxs("div", {
                                            className: "flex items-center justify-between gap-3",
                                            children: [
                                                /*#__PURE__*/ jsx(Span, {
                                                    textStyle: "body3",
                                                    children: "Price per night"
                                                }),
                                                /*#__PURE__*/ jsx(Span, {
                                                    textStyle: "body3",
                                                    children: rangeLabel
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ jsx(Slider, {
                                            min: 0,
                                            max: ceiling,
                                            step: 1,
                                            value: draft,
                                            labelText: "Your budget (per night)",
                                            ariaLabelledby: "price-filter-budget",
                                            slotClassNames: {
                                                labelClass: 'sr-only'
                                            },
                                            handleChange: (value)=>{
                                                if (Array.isArray(value) && value.length >= 2) setDraft([
                                                    value[0] ?? 0,
                                                    value[1] ?? ceiling
                                                ]);
                                            }
                                        }),
                                        /*#__PURE__*/ jsx(Popover.Close, {
                                            "aria-label": "Apply",
                                            render: /*#__PURE__*/ jsx(Button, {
                                                type: "primary",
                                                size: "md",
                                                fullWidth: true,
                                                buttonType: "button",
                                                onClick: ()=>{
                                                    onApply({
                                                        minPrice: draft[0],
                                                        ...draft[1] < ceiling ? {
                                                            maxPrice: draft[1]
                                                        } : {}
                                                    });
                                                },
                                                children: "Apply"
                                            })
                                        })
                                    ]
                                }) : /*#__PURE__*/ jsxs("div", {
                                    className: "flex flex-col gap-4",
                                    children: [
                                        /*#__PURE__*/ jsx(Span, {
                                            textStyle: "body2",
                                            children: "Choose your destination to filter by price."
                                        }),
                                        /*#__PURE__*/ jsx(Popover.Close, {
                                            "aria-label": "Dismiss",
                                            render: /*#__PURE__*/ jsx(Button, {
                                                type: "primary",
                                                size: "md",
                                                fullWidth: true,
                                                buttonType: "button",
                                                children: "Dismiss"
                                            })
                                        })
                                    ]
                                })
                            })
                        ]
                    })
                })
            })
        ]
    });
}
export { PriceFilterControl, resolveSessionCurrency };
